// Lib
import { createSelector, createStructuredSelector } from 'reselect';

// Utils
import { prop } from '../../../common/utils/immutableHelper';
import { isClone } from '../../../common/elements/utils/elementTypeUtils';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getLinkedElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { getRenderedCloneElement } from '../clone/cloneUtils';
import { isAsyncEntityFetched } from '../../utils/services/http/asyncResource/asyncResourceUtils';

// Selectors
import { selectIsSocketConnected } from '../../utils/socket/socketConnectionSelector';
import { elementsAsyncResourcesSelector, getElements, isElementSelectedSelector } from '../selectors/elementSelector';
import { getCurrentModalElementId } from './elementModalCommonSelector';
import { canWrite } from '../../../common/permissions/permissionUtil';
import { getIsElectronSelector } from '../../components/electron/electronSelector';
import { getSpellCheckStatus } from '../../../common/electron/electronSelectors';
import { getUrlPermissionId } from '../../app/routingSelector';
import { currentBoardUserPermissionsSelector } from '../../utils/permissions/elementPermissionsSelector';
import { getLocalCacheHydrationTimestamp } from '../../offline/cache/localCacheSelector';

const currentElementIdSelector = (state: any, props: any) =>
    getCurrentModalElementId(state) || props?.elementId || (props?.routeParams && props?.routeParams.elementId);

const getCurrentElement = createSelector(currentElementIdSelector, getElements, prop);

const getCurrentElementResourceEntityState = createSelector(
    elementsAsyncResourcesSelector,
    currentElementIdSelector,
    (elementResourcesState, currentElementId) => elementResourcesState?.[currentElementId],
);

const getIsCurrentElementFetched = createSelector(getCurrentElementResourceEntityState, isAsyncEntityFetched);

const getCurrentElementPermissions = (state: any) => {
    return currentBoardUserPermissionsSelector(state);
};

const getIsRemotelySelected = createSelector(
    currentElementIdSelector,
    (state) => state.getIn(['remoteActivity', 'selectedElements']),
    (elementId, remoteElementActivity) => {
        const remoteSelectionData = remoteElementActivity.get(elementId);
        return remoteSelectionData && !!remoteSelectionData.get('userId');
    },
);

const getCurrentElementIsEditable = createSelector(
    getCurrentElementPermissions,
    getIsRemotelySelected,
    (permissions, isRemotelySelected) => canWrite(permissions) && !isRemotelySelected,
);

const getSpellCheck = createSelector(getIsElectronSelector, getSpellCheckStatus, (isElectron, spellCheckStatus) =>
    isElectron ? spellCheckStatus : true,
);

const getRenderedElement = createSelector(getCurrentElement, getElements, (currentElement, elements) => {
    if (!isClone(currentElement)) return currentElement;

    const linkedElementId = getLinkedElementId(currentElement);
    const linkedElement = getElement(elements, linkedElementId);

    return getRenderedCloneElement(currentElement, linkedElement);
});

// @ts-ignore - a bunch of unknown types here, but it's fine
export default createStructuredSelector({
    elementId: currentElementIdSelector,
    urlPermissionId: getUrlPermissionId,
    element: getRenderedElement,

    isElementSelected: isElementSelectedSelector(),

    localCacheHydrationTimestamp: getLocalCacheHydrationTimestamp,
    currentElementResourceEntity: getCurrentElementResourceEntityState,
    isElementFetched: getIsCurrentElementFetched,

    permissions: getCurrentElementPermissions,
    isEditable: getCurrentElementIsEditable,

    socketConnected: selectIsSocketConnected,
    spellCheck: getSpellCheck,
});
